import { useState, type CSSProperties } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { DoorOpen, Volume2, VolumeX, LogOut, ChevronDown } from 'lucide-react'
import { getActiveWorldConfig } from '@gamecodex/world-config'
import { userAvatarUrl } from '@/lib/avatar/startingAvatar'
import { authHrefWithReturn, currentReturnPath, readRequestedReturnPath } from '@/lib/auth/returnTo'
import { cn } from '@/lib/cn'
import { useUserStore } from '@/stores/userStore'
import { useCoinStore } from '@/stores/coinStore'
import { useAudioStore } from '@/stores/audioStore'
import { useCoinBalanceSync } from '@/lib/api/coins'
import { useAuth } from '@/lib/auth/useAuth'
import { useT, useLocalizedHref } from '@/i18n'
import { GAME_TABS, useGameNav } from '@/features/city/gameNav'

// The game header (EvolveRPG, from the sliced header kit in public/brand/header), shown over the city game only (Jared,
// 11/2: the header belongs to the game, not the site). The painted frame, wordmark, icons, coin emblem and sign-out
// plate are art cut from the kit at 150 px tall and drawn at the header's height; every word, the coin count, the
// avatar and the name are live. It is the only header in the game (the site's own is gone there): the five tabs drive
// the game (features/city/gameNav.ts), the door plate on the right exits to the website, and the menu plate holds sound
// and sign out (and the tabs, on screens too narrow for them).
const KIT_H = 150 // the kit's own height: every piece's width is its kit width scaled by the header height over this
const w = (kitPx: number): CSSProperties => ({ width: `calc(var(--site-header-h, 4rem) * ${kitPx / KIT_H})` })

/** Where Exit goes: the website's world page, where the cities are entered from. */
const EXIT_TO = '/world'

export function KitHeader({ kit }: { kit: string }) {
  const [menuOpen, setMenuOpen] = useState(false)
  const user = useUserStore((s) => s.user)
  const balance = useCoinStore((s) => s.balance)
  const balanceKnown = useCoinStore((s) => s.hydrated)
  useCoinBalanceSync(user?.id)
  const muted = useAudioStore((s) => s.muted)
  const toggleMuted = useAudioStore((s) => s.toggleMuted)
  const { signOut } = useAuth()
  const navigate = useNavigate()
  const game = useGameNav()
  const t = useT()
  const localized = useLocalizedHref()
  const location = useLocation()
  const loginHref = localized(authHrefWithReturn('/login', readRequestedReturnPath(location.state, location.search) ?? currentReturnPath(location)))
  const { branding, worldName } = getActiveWorldConfig()
  const art = (name: string) => `${kit}/${name}.webp`
  const out = () => { setMenuOpen(false); void signOut().then(() => navigate(localized('/'))) }

  return (
    <header data-site-header data-header-kit className="sticky top-0 z-40 select-none shadow-[0_4px_16px_rgba(0,0,0,0.6)]"
      style={{ backgroundImage: `url(${art('nav-bg')})`, backgroundSize: 'auto 100%', backgroundRepeat: 'repeat-x', backgroundColor: '#0b0d10' }}>
      <div className="relative flex h-[var(--site-header-h,4rem)] w-full items-stretch">
        {/* the menu plate and the wordmark: one painted piece, two controls over it */}
        <div className="relative shrink-0" style={{ ...w(520), backgroundImage: `url(${art('left')})`, backgroundSize: '100% 100%' }}>
          <button type="button" onClick={() => setMenuOpen((v) => !v)} aria-expanded={menuOpen} aria-label={t('layout.nav.toggleMenu')}
            className="group absolute inset-y-0 left-0" style={w(116)}>
            <span aria-hidden className={cn('absolute inset-0 bg-[length:100%_100%] transition-opacity', menuOpen ? 'opacity-100' : 'opacity-0 group-hover:opacity-100')} style={{ backgroundImage: `url(${art('menu-lit')})` }} />
          </button>
          <Link to={localized('/')} aria-label={t('layout.nav.homeAria', { world: worldName })} className="absolute inset-y-0 transition-[filter] hover:brightness-110" style={{ left: `calc(var(--site-header-h, 4rem) * ${116 / KIT_H})`, ...w(404) }} />
        </div>

        <ul className="hidden items-stretch lg:flex">
          {GAME_TABS.map((tab, i) => { const isActive = !!game?.[tab].on; return (
            <li key={tab} className={cn('relative flex', i > 0 && 'before:absolute before:inset-y-[30%] before:left-0 before:w-px before:bg-gradient-to-b before:from-transparent before:via-gold/30 before:to-transparent')}>
              <button type="button" onClick={() => game?.[tab].go()} disabled={!game} aria-pressed={isActive} data-game-tab={tab}
                className={cn('group relative flex min-w-[5.5rem] flex-col items-center justify-center gap-[2px] px-4 pb-[3px] font-heading text-[12px] leading-none tracking-wide transition-colors disabled:opacity-50 xl:min-w-[6.5rem] xl:text-[13px]',
                  isActive ? 'text-[#fff1d0]' : 'text-[#d8ccb2]/75 hover:text-[#fff1d0]')}>
                {(
                  <>
                    <img src={art(`icon-${tab}`)} alt="" aria-hidden draggable={false}
                      className={cn('h-[50%] w-auto transition-[filter]', isActive ? 'drop-shadow-[0_0_6px_rgba(255,170,60,0.85)]' : 'brightness-75 saturate-50 group-hover:brightness-100 group-hover:saturate-100')} />
                    <span>{t(`layout.kit.${tab}`)}</span>
                    {/* the glowing bar under the open tab, just above the frame's lower rail */}
                    <span aria-hidden className={cn('absolute inset-x-3 bottom-[9%] h-[2px] rounded-full bg-gradient-to-r from-transparent via-[#ffb347] to-transparent shadow-[0_0_8px_2px_rgba(255,150,40,0.55)] transition-opacity', isActive ? 'opacity-100' : 'opacity-0')} />
                  </>
                )}
              </button>
            </li>
          ) })}
        </ul>

        <div className="flex-1" />

        <img src={art('right-start')} alt="" aria-hidden draggable={false} className="h-full shrink-0" style={w(42)} />
        <div className="flex shrink-0 items-center gap-2 pr-2 sm:gap-3" style={{ backgroundImage: `url(${art('right-bg')})`, backgroundSize: 'auto 100%', backgroundRepeat: 'repeat-x' }}>
          <button type="button" onClick={toggleMuted} aria-label={muted ? t('layout.nav.unmute') : t('layout.nav.mute')} className="hidden rounded-sm p-1.5 text-[#d8ccb2]/70 hover:text-gold md:block">
            {muted ? <VolumeX size={17} /> : <Volume2 size={17} />}
          </button>
          {user ? (
            <>
              <Link to={localized('/me/coins')} title={balanceKnown ? t('layout.nav.coinBalance') : t('layout.nav.coinBalanceUnavailable')}
                aria-label={balanceKnown ? `${t('layout.nav.coinBalance')}: ${balance}` : t('layout.nav.coinBalanceUnavailable')}
                className="hidden h-[68%] items-center gap-1.5 rounded-[3px] border border-[#c9a45c]/70 bg-black/55 px-2 shadow-[inset_0_0_0_1px_rgba(0,0,0,0.8),inset_0_0_10px_rgba(0,0,0,0.9),0_0_0_1px_rgba(120,85,30,0.5)] transition-colors hover:border-[#f1c776] sm:flex">
                <img src={art('coin')} alt="" aria-hidden draggable={false} className="h-[70%] w-auto" />
                <span className="font-heading text-[15px] font-semibold tabular-nums text-[#f5c04a] [text-shadow:0_0_6px_rgba(245,170,40,0.45)]">{balanceKnown ? balance.toLocaleString() : '?'}</span>
              </Link>
              <Link to={localized(getActiveWorldConfig().playerHome ?? '/me')} title={t('layout.nav.signedInAs', { name: user.displayName })} className="group flex h-full items-center gap-2">
                <img src={userAvatarUrl(user) ?? branding.images.avatarFallback} alt=""
                  className="aspect-square h-[74%] rounded-full border-2 border-[#d9b36a] object-cover shadow-[0_0_0_2px_rgba(20,14,8,0.9),0_0_0_3px_rgba(150,110,45,0.7),0_0_10px_rgba(255,180,80,0.3)] transition-shadow group-hover:shadow-[0_0_0_2px_rgba(20,14,8,0.9),0_0_0_3px_rgba(220,170,80,0.9),0_0_14px_rgba(255,180,80,0.55)]" />
                <span className="hidden max-w-[9rem] truncate font-heading text-[15px] font-semibold text-[#f3ead8] sm:inline">{user.displayName}</span>
                <ChevronDown size={15} aria-hidden className="hidden text-[#d9b36a] sm:block" />
              </Link>
            </>
          ) : (
            <Link to={loginHref} className="btn-metal px-3 py-1.5 text-xs">{t('layout.nav.enter', { world: worldName })}</Link>
          )}
        </div>
        {/* the door plate: out of the game, back to the website */}
        <Link to={localized(EXIT_TO)} aria-label={t('layout.kit.exit')} title={t('layout.kit.exit')} data-game-exit className="group relative shrink-0" style={{ ...w(117), backgroundImage: `url(${art('logout-unlit')})`, backgroundSize: '100% 100%' }}>
          <span aria-hidden className="absolute inset-0 bg-[length:100%_100%] opacity-0 transition-opacity group-hover:opacity-100" style={{ backgroundImage: `url(${art('logout-active')})` }} />
        </Link>
      </div>

      {menuOpen && (
        <nav aria-label={t('layout.nav.toggleMenu')} className="absolute left-0 top-full w-full max-w-xs border border-t-0 border-[#c9a45c]/40 bg-[#0d1014] px-4 py-3 shadow-[0_10px_24px_rgba(0,0,0,0.7)] backdrop-blur-md">
          <ul className="lg:hidden">
            {GAME_TABS.map((tab) => (
              <li key={tab}>
                <button type="button" disabled={!game} onClick={() => { setMenuOpen(false); game?.[tab].go() }}
                  className={cn('flex w-full items-center gap-3 py-2 font-heading text-sm disabled:opacity-50', game?.[tab].on ? 'text-[#fff1d0]' : 'text-[#d8ccb2]/80 hover:text-[#fff1d0]')}>
                  <img src={art(`icon-${tab}`)} alt="" aria-hidden className="h-5 w-auto" />{t(`layout.kit.${tab}`)}
                </button>
              </li>
            ))}
          </ul>
          <Link to={localized(EXIT_TO)} onClick={() => setMenuOpen(false)} className="mt-1 flex items-center gap-3 border-t border-[#c9a45c]/25 py-2 pt-3 font-heading text-sm text-[#d8ccb2]/80 hover:text-[#fff1d0] lg:mt-0 lg:border-0 lg:pt-2">
            <DoorOpen size={18} aria-hidden className="text-[#d9b36a]" />{t('layout.kit.exit')}
          </Link>
          <div className="mt-1 flex items-center gap-1 border-t border-[#c9a45c]/25 pt-2">
            <button type="button" onClick={toggleMuted} aria-label={muted ? t('layout.nav.unmute') : t('layout.nav.mute')} className="rounded-sm p-2 text-bone-muted hover:text-gold">
              {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
            </button>
            {user && <button type="button" onClick={out} className="ml-auto flex items-center gap-2 rounded-sm p-2 font-ui text-xs uppercase tracking-wide text-bone-muted hover:text-gold"><LogOut size={16} />{t('layout.nav.signOut')}</button>}
          </div>
        </nav>
      )}
    </header>
  )
}
